import { useEffect, useState, useCallback } from "react";
import { http, downloadAuthenticated } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  FileText,
  Filter,
  Search,
  RefreshCw,
  Trash2,
  Download,
  Save,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const CATEGORIES = ["ALL", "FTEXT", "ORDER", "ERROR", "USER", "SYSTEM"];
const LEVELS = ["all", "info", "warn", "error"];

const CAT_COLOR = {
  FTEXT: "bg-blue-100 text-blue-800 border-blue-300",
  ORDER: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ERROR: "bg-red-100 text-red-800 border-red-300",
  USER: "bg-violet-100 text-violet-800 border-violet-300",
  SYSTEM: "bg-slate-100 text-slate-800 border-slate-300",
};

const LVL_COLOR = {
  info: "bg-slate-100 text-slate-700 border-slate-300",
  warn: "bg-amber-100 text-amber-800 border-amber-300",
  error: "bg-red-100 text-red-800 border-red-300",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("el-GR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function LogsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, by_category: {}, by_level: {} });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [retention, setRetention] = useState(30);
  const [savingRet, setSavingRet] = useState(false);

  // Filters
  const [category, setCategory] = useState("ALL");
  const [level, setLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit] = useState(200);

  const loadStats = useCallback(async () => {
    try {
      const r = await http.get("/logs/stats");
      setStats(r.data);
    } catch {
      /* noop */
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await http.get("/settings");
      setRetention(r.data.logs_retention_days ?? 30);
    } catch {
      /* noop */
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (category !== "ALL") params.category = category;
      if (level !== "all") params.level = level;
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        // include the entire end day
        const dt = new Date(dateTo);
        dt.setHours(23, 59, 59, 999);
        params.date_to = dt.toISOString();
      }
      const r = await http.get("/logs", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης logs");
    } finally {
      setLoading(false);
    }
  }, [category, level, search, dateFrom, dateTo, limit]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchLogs();
      loadStats();
      loadSettings();
    }, 0);
    return () => clearTimeout(t);
  }, [fetchLogs, loadStats, loadSettings]);

  const onClearAll = async () => {
    if (!(await confirmDialog("Διαγραφή ΟΛΩΝ των logs;"))) return;
    try {
      const r = await http.delete("/logs");
      toast.success(`Διαγράφηκαν ${r.data.deleted} εγγραφές`);
      fetchLogs();
      loadStats();
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const onClearOld = async () => {
    const days = (await promptDialog("Διαγραφή logs παλαιότερων από πόσες ημέρες;", "30"));
    if (!days) return;
    const n = Number(days);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Άκυρος αριθμός ημερών");
      return;
    }
    try {
      const r = await http.delete(`/logs?older_than_days=${n}`);
      toast.success(`Διαγράφηκαν ${r.data.deleted} εγγραφές`);
      fetchLogs();
      loadStats();
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const onExport = (fmt) => {
    const params = { fmt };
    if (category !== "ALL") params.category = category;
    if (level !== "all") params.level = level;
    if (search.trim()) params.search = search.trim();
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
    if (dateTo) {
      const dt = new Date(dateTo);
      dt.setHours(23, 59, 59, 999);
      params.date_to = dt.toISOString();
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const ext = fmt === "csv" ? "csv" : "xlsx";
    downloadAuthenticated(
      "/logs/export",
      params,
      `logs-${stamp}.${ext}`,
      { folder: "reports" },
    )
      .then((r) => toast.success(
        r.viaCompanion
          ? `Αποθηκεύτηκε: ${r.savedTo}`
          : `Λήφθηκε το ${ext.toUpperCase()}`,
      ))
      .catch((e) => toast.error(
        e?.message?.includes("Companion")
          ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
          : "Αποτυχία εξαγωγής",
      ));
  };

  const onSaveRetention = async () => {
    setSavingRet(true);
    try {
      const s = await http.get("/settings");
      const next = { ...s.data, logs_retention_days: Number(retention) || 0 };
      await http.put("/settings", next);
      toast.success("Αποθηκεύτηκε");
    } catch {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setSavingRet(false);
    }
  };

  const resetFilters = () => {
    setCategory("ALL");
    setLevel("all");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <Card className="p-6 space-y-4" data-testid="logs-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <FileText size={18} /> Logs Εφαρμογής
        </h3>
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          <span className="px-2 py-1 rounded bg-slate-100 border border-slate-300 font-bold">
            Σύνολο: {stats.total}
          </span>
          {Object.entries(stats.by_category).map(([k, v]) => (
            <span
              key={k}
              className={`px-2 py-1 rounded border font-bold ${CAT_COLOR[k] || CAT_COLOR.SYSTEM}`}
            >
              {k}: {v}
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div>
          <Label className="text-xs">Κατηγορία</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm"
            data-testid="logs-filter-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c === "ALL" ? "Όλες" : c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Επίπεδο</Label>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm"
            data-testid="logs-filter-level"
          >
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l === "all" ? "Όλα" : l}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Από</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 text-sm"
            data-testid="logs-filter-from"
          />
        </div>
        <div>
          <Label className="text-xs">Έως</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 text-sm"
            data-testid="logs-filter-to"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Αναζήτηση</Label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
              placeholder="κείμενο μηνύματος..."
              className="h-9 pl-7 text-sm"
              data-testid="logs-filter-search"
            />
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          onClick={fetchLogs}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 h-9"
          data-testid="logs-refresh-btn"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Ανανέωση
        </Button>
        <Button
          onClick={() => resetFilters()}
          variant="outline"
          className="h-9"
          data-testid="logs-reset-btn"
        >
          <Filter size={14} className="mr-1.5" />
          Καθαρισμός φίλτρων
        </Button>
        <div className="flex-1" />
        <Button
          onClick={() => onExport("csv")}
          variant="outline"
          className="h-9"
          data-testid="logs-export-csv"
        >
          <Download size={14} className="mr-1.5" /> CSV
        </Button>
        <Button
          onClick={() => onExport("txt")}
          variant="outline"
          className="h-9"
          data-testid="logs-export-txt"
        >
          <Download size={14} className="mr-1.5" /> TXT
        </Button>
        <Button
          onClick={onClearOld}
          variant="outline"
          className="h-9 text-amber-700 hover:bg-amber-50"
          data-testid="logs-clear-old-btn"
        >
          <Trash2 size={14} className="mr-1.5" /> Παλαιά...
        </Button>
        <Button
          onClick={onClearAll}
          variant="outline"
          className="h-9 text-red-700 hover:bg-red-50"
          data-testid="logs-clear-all-btn"
        >
          <Trash2 size={14} className="mr-1.5" /> Όλα
        </Button>
      </div>

      {/* Retention */}
      <div className="flex items-end gap-2 p-3 bg-slate-50 border border-slate-200 rounded-md">
        <div className="flex-1">
          <Label className="text-xs">Αυτόματη διαγραφή logs μετά από (ημέρες)</Label>
          <div className="text-[11px] text-slate-500">
            0 = κρατάει όλα τα logs για πάντα. Εκτελείται σε κάθε εκκίνηση της εφαρμογής.
          </div>
        </div>
        <Input
          type="number"
          min={0}
          value={retention}
          onChange={(e) => setRetention(e.target.value)}
          className="h-9 w-28"
          data-testid="logs-retention-input"
        />
        <Button
          onClick={onSaveRetention}
          disabled={savingRet}
          className="bg-emerald-600 hover:bg-emerald-700 h-9"
          data-testid="logs-retention-save"
        >
          <Save size={14} className="mr-1.5" /> Αποθήκευση
        </Button>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-[160px_70px_80px_1fr_36px] gap-2 px-3 py-2 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <div>Ημερομηνία</div>
          <div>Επίπεδο</div>
          <div>Κατηγορία</div>
          <div>Μήνυμα</div>
          <div></div>
        </div>
        <div
          className="max-h-[55vh] overflow-y-auto scroll-thin divide-y divide-slate-100"
          data-testid="logs-table"
        >
          {items.length === 0 && !loading && (
            <div className="p-6 text-center text-slate-400 text-sm">Καμία εγγραφή</div>
          )}
          {items.map((it) => {
            const open = !!expanded[it.id];
            const hasDetails = it.details && Object.keys(it.details).length > 0;
            return (
              <div key={it.id} className="text-sm">
                <button
                  type="button"
                  className="w-full text-left grid grid-cols-[160px_70px_80px_1fr_36px] gap-2 px-3 py-2 hover:bg-slate-50"
                  onClick={() =>
                    setExpanded((s) => ({ ...s, [it.id]: !s[it.id] }))
                  }
                  data-testid="logs-row"
                >
                  <div className="font-mono text-xs text-slate-600">
                    {fmtDate(it.timestamp)}
                  </div>
                  <div>
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${
                        LVL_COLOR[it.level] || LVL_COLOR.info
                      }`}
                    >
                      {it.level}
                    </span>
                  </div>
                  <div>
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                        CAT_COLOR[it.category] || CAT_COLOR.SYSTEM
                      }`}
                    >
                      {it.category}
                    </span>
                  </div>
                  <div className="truncate">
                    {it.source && (
                      <span className="text-[11px] text-slate-400 font-mono mr-2">
                        {it.source}
                      </span>
                    )}
                    {it.message}
                  </div>
                  <div className="text-slate-400">
                    {hasDetails ? (
                      open ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                    ) : null}
                  </div>
                </button>
                {open && hasDetails && (
                  <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
                    <pre className="text-[11px] font-mono whitespace-pre-wrap break-all text-slate-700">
                      {JSON.stringify(it.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-3 py-2 bg-slate-50 text-[11px] text-slate-500 border-t border-slate-200">
          Εμφανίζονται {items.length} από {total} εγγραφές
          {items.length === limit && total > limit && (
            <span className="ml-2">(όριο {limit} – στενέψτε τα φίλτρα για περισσότερα)</span>
          )}
        </div>
      </div>
    </Card>
  );
}
